import {
  addDoc,
  collection,
  deleteDoc,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase/config";
import AIService from "./AIService";
import type { App } from "../types/app";
import {
  AdvancedExecutionStep,
  AdvancedExperiment,
  AdvancedFeatureDefinition,
  AdvancedFeatureId,
  AdvancedLabInput,
  AdvancedLabOutput,
  AdvancedLabRun,
} from "../types/advanced";
import { buildAppContextLines } from "../utils/appContextPrompt";

const COLLECTION = "advancedGrowthWorkspaces";

export const ADVANCED_FEATURE_DEFINITIONS: AdvancedFeatureDefinition[] = [
  {
    id: "context-hub",
    label: "Product Context Hub",
    description:
      "Build a reusable product-marketing context document that aligns messaging, audience, and positioning across all generators.",
    focusArea: "Strategy Foundation",
    relatedSkills: ["product-marketing-context"],
    promptInstructions: [
      "Define one clear market category and positioning statement.",
      "List direct, secondary, and indirect alternatives with differentiation.",
      "Capture customer language to use and avoid.",
    ],
  },
  {
    id: "analytics-tracking-planner",
    label: "Analytics Tracking Planner",
    description:
      "Generate an end-to-end analytics instrumentation strategy with event taxonomy and conversion measurement plan.",
    focusArea: "Measurement",
    relatedSkills: ["analytics-tracking"],
    promptInstructions: [
      "Return event naming standards and event properties.",
      "Include channel attribution and UTM strategy.",
      "Map every KPI to at least one measurable event.",
    ],
  },
  {
    id: "experiment-workbench",
    label: "Experiment Workbench",
    description:
      "Create statistically grounded CRO tests with hypotheses, guardrails, and execution-ready experiment cards.",
    focusArea: "Optimization",
    relatedSkills: [
      "ab-test-setup",
      "page-cro",
      "signup-flow-cro",
      "form-cro",
    ],
    promptInstructions: [
      "Prioritize high-impact experiments first.",
      "Each experiment must include primary KPI and one guardrail KPI.",
      "Tie hypotheses to known user friction points.",
    ],
  },
  {
    id: "marketing-optimizer",
    label: "Marketing Closed-Loop Optimizer",
    description:
      "Translate campaign performance insights into next-wave creative and targeting recommendations.",
    focusArea: "Acquisition",
    relatedSkills: ["ad-creative", "paid-ads"],
    promptInstructions: [
      "Recommend what to scale, pause, and iterate.",
      "Break out actions by channel and audience segment.",
      "Focus on lowering CAC and improving conversion quality.",
    ],
  },
  {
    id: "pseo-publisher",
    label: "Programmatic SEO Publisher",
    description:
      "Plan scalable SEO page systems with schema, internal linking, and publication workflow.",
    focusArea: "Organic Growth",
    relatedSkills: ["programmatic-seo", "schema-markup", "ai-seo"],
    promptInstructions: [
      "Propose template families with variables and quality controls.",
      "Include schema and internal link directives.",
      "Define an execution cadence for indexing and updates.",
    ],
  },
  {
    id: "competitor-page-builder",
    label: "Competitor Alternatives Builder",
    description:
      "Create high-conversion competitor and alternatives content strategy for SEO and sales enablement.",
    focusArea: "Organic Growth",
    relatedSkills: ["competitor-alternatives"],
    promptInstructions: [
      "Suggest page formats: alternative, versus, comparison hub.",
      "Provide defensible messaging angles and proof themes.",
      "Avoid generic feature-table-only structure.",
    ],
  },
  {
    id: "content-calendar",
    label: "Content Calendar Engine",
    description:
      "Generate a 30/60/90-day multi-channel content strategy mapped to funnel stages.",
    focusArea: "Content",
    relatedSkills: ["content-strategy", "social-content", "email-sequence"],
    promptInstructions: [
      "Balance searchable and shareable content.",
      "Map each content asset to one conversion action.",
      "Include repurposing paths across channels.",
    ],
  },
  {
    id: "social-repurposer",
    label: "Social Repurposing Engine",
    description:
      "Repurpose existing messaging and assets into platform-native social content systems.",
    focusArea: "Social",
    relatedSkills: ["social-content"],
    promptInstructions: [
      "Define pillar-based post ideas and hooks.",
      "Specify per-platform adaptation guidance.",
      "Include frequency and sequencing recommendations.",
    ],
  },
  {
    id: "outreach-assistant",
    label: "Outreach Sequence Assistant",
    description:
      "Generate personalized creator/prospect outreach and follow-up sequences.",
    focusArea: "Outbound",
    relatedSkills: ["cold-email", "email-sequence"],
    promptInstructions: [
      "Write concise value-first outreach structure.",
      "Include personalization variables and fallback variants.",
      "Sequence follow-ups with clear stop conditions.",
    ],
  },
  {
    id: "pricing-strategist",
    label: "Pricing & Packaging Strategist",
    description:
      "Produce pricing experiments, tier packaging options, and rollout messaging.",
    focusArea: "Monetization",
    relatedSkills: ["pricing-strategy", "marketing-psychology"],
    promptInstructions: [
      "Recommend value metrics and tier boundaries.",
      "Outline experimentation to validate willingness to pay.",
      "Include risk mitigation for price changes.",
    ],
  },
  {
    id: "paywall-optimizer",
    label: "Paywall Optimization Planner",
    description:
      "Create in-product paywall strategies with triggers, copy angles, and conversion experiments.",
    focusArea: "Monetization",
    relatedSkills: ["paywall-upgrade-cro"],
    promptInstructions: [
      "Define trigger moments aligned to value realization.",
      "Propose variant messaging for user segments.",
      "Include anti-patterns to avoid.",
    ],
  },
  {
    id: "onboarding-optimizer",
    label: "Onboarding Activation Optimizer",
    description:
      "Design activation-focused onboarding systems and testable improvements.",
    focusArea: "Activation",
    relatedSkills: ["onboarding-cro"],
    promptInstructions: [
      "Define activation events and milestones.",
      "Design friction-reduction interventions for first-session users.",
      "Prioritize fastest time-to-value improvements.",
    ],
  },
  {
    id: "churn-prevention",
    label: "Churn Prevention Studio",
    description:
      "Build voluntary and involuntary churn reduction strategies including save offers and dunning.",
    focusArea: "Retention",
    relatedSkills: ["churn-prevention", "email-sequence"],
    promptInstructions: [
      "Differentiate strategies for voluntary vs involuntary churn.",
      "Add cancel-flow interventions and win-back motions.",
      "Track churn diagnostics and cohort outcomes.",
    ],
  },
  {
    id: "referral-builder",
    label: "Referral Program Builder",
    description:
      "Design referral/affiliate systems with incentive economics and launch framework.",
    focusArea: "Growth Loops",
    relatedSkills: ["referral-program"],
    promptInstructions: [
      "Define reward mechanics and abuse prevention.",
      "Map referral funnel and conversion checkpoints.",
      "Include lifecycle communications for advocates.",
    ],
  },
  {
    id: "copy-quality-gate",
    label: "Copy Quality Gate",
    description:
      "Add a final quality pass for clarity, persuasion, consistency, and policy-safe language.",
    focusArea: "Messaging",
    relatedSkills: ["copywriting", "copy-editing"],
    promptInstructions: [
      "Score copy on clarity, relevance, and specificity.",
      "Identify weak claims and rewrite patterns.",
      "Return prioritized copy fixes by impact.",
    ],
  },
  {
    id: "readiness-quality-score",
    label: "Publish Readiness Quality Score",
    description:
      "Score launch readiness by quality and conversion fitness, not only artifact completeness.",
    focusArea: "Launch Quality",
    relatedSkills: ["page-cro", "schema-markup", "copy-editing"],
    promptInstructions: [
      "Evaluate messaging quality, conversion UX, and compliance confidence.",
      "Return weighted scoring dimensions with rationale.",
      "Provide targeted improvements for low-scoring areas.",
    ],
  },
  {
    id: "fcm-lifecycle-automation",
    label: "FCM Lifecycle Automation Planner",
    description:
      "Design production-grade push lifecycle strategy with sequencing, throttling, and performance measurement.",
    focusArea: "Engagement",
    relatedSkills: ["analytics-tracking", "onboarding-cro"],
    promptInstructions: [
      "Define lifecycle campaigns by user state and trigger.",
      "Include pacing, suppression, and fatigue guardrails.",
      "Map campaign analytics to business outcomes.",
    ],
  },
  {
    id: "legal-jurisdiction-generator",
    label: "Legal Documentation Strategist",
    description:
      "Create a jurisdiction-aware legal content strategy and review checklist for app launches.",
    focusArea: "Compliance",
    relatedSkills: ["copywriting"],
    promptInstructions: [
      "Outline legal document requirements by region.",
      "Highlight compliance risks and remediation priorities.",
      "Provide review cadence and ownership.",
    ],
  },
  {
    id: "screenshot-pipeline-optimizer",
    label: "Screenshot Pipeline Optimizer",
    description:
      "Improve screenshot generation quality, localization consistency, and experimentation velocity.",
    focusArea: "Store Conversion",
    relatedSkills: ["copywriting", "social-content"],
    promptInstructions: [
      "Define screenshot messaging hierarchy by sequence position.",
      "Recommend localization and quality QA standards.",
      "Include screenshot A/B testing roadmap.",
    ],
  },
  {
    id: "weekly-growth-operator",
    label: "Weekly AI Growth Operator",
    description:
      "Generate a weekly prioritized growth operating plan across acquisition, activation, and retention.",
    focusArea: "Operations",
    relatedSkills: ["marketing-ideas"],
    promptInstructions: [
      "Rank actions by impact and implementation effort.",
      "Assign ownership and weekly timelines.",
      "Keep plan execution-focused and measurable.",
    ],
  },
];

export const DEFAULT_ADVANCED_FEATURE_ID: AdvancedFeatureId =
  "weekly-growth-operator";

export const DEFAULT_ADVANCED_INPUT: AdvancedLabInput = {
  primaryGoal: "",
  targetAudience: "",
  budgetRange: "",
  timeline: "Next 90 days",
  currentMetrics: "",
  constraints: "",
  channels: [],
  notes: "",
};

const ADVANCED_OUTPUT_SCHEMA = {
  type: "object",
  properties: {
    summary: { type: "string" },
    diagnosis: { type: "array", items: { type: "string" } },
    strategy: { type: "array", items: { type: "string" } },
    experiments: {
      type: "array",
      items: {
        type: "object",
        properties: {
          name: { type: "string" },
          hypothesis: { type: "string" },
          implementationSteps: { type: "array", items: { type: "string" } },
          primaryKpi: { type: "string" },
          guardrailKpi: { type: "string" },
          priority: { type: "string", enum: ["high", "medium", "low"] },
        },
        required: [
          "name",
          "hypothesis",
          "implementationSteps",
          "primaryKpi",
          "guardrailKpi",
          "priority",
        ],
      },
    },
    executionPlan: {
      type: "array",
      items: {
        type: "object",
        properties: {
          weekLabel: { type: "string" },
          objective: { type: "string" },
          tasks: { type: "array", items: { type: "string" } },
        },
        required: ["weekLabel", "objective", "tasks"],
      },
    },
    assetDeliverables: { type: "array", items: { type: "string" } },
    trackingPlan: {
      type: "object",
      properties: {
        primaryMetrics: { type: "array", items: { type: "string" } },
        secondaryMetrics: { type: "array", items: { type: "string" } },
        eventTracking: { type: "array", items: { type: "string" } },
      },
      required: ["primaryMetrics", "secondaryMetrics", "eventTracking"],
    },
    risks: { type: "array", items: { type: "string" } },
    nextActions: { type: "array", items: { type: "string" } },
  },
  required: [
    "summary",
    "diagnosis",
    "strategy",
    "experiments",
    "executionPlan",
    "assetDeliverables",
    "trackingPlan",
    "risks",
    "nextActions",
  ],
};

class AdvancedGrowthService {
  static getFeatureDefinition(featureId: AdvancedFeatureId): AdvancedFeatureDefinition {
    return (
      ADVANCED_FEATURE_DEFINITIONS.find((item) => item.id === featureId) ||
      ADVANCED_FEATURE_DEFINITIONS[0]
    );
  }

  private static generateId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  private static toDate(value: any): Date {
    if (value instanceof Timestamp) return value.toDate();
    if (value instanceof Date) return value;
    return new Date(value);
  }

  private static normalizeStringArray(value: unknown, limit = 10): string[] {
    if (!Array.isArray(value)) return [];
    return value
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .slice(0, limit);
  }

  private static normalizeExperiment(item: any): AdvancedExperiment | null {
    if (!item || typeof item !== "object") return null;
    const rawPriority = String(item.priority || "medium").toLowerCase();
    const priority: "high" | "medium" | "low" =
      rawPriority === "high" || rawPriority === "low" ? rawPriority : "medium";
    const experiment: AdvancedExperiment = {
      name: String(item.name || "").trim(),
      hypothesis: String(item.hypothesis || "").trim(),
      implementationSteps: this.normalizeStringArray(item.implementationSteps, 8),
      primaryKpi: String(item.primaryKpi || "").trim(),
      guardrailKpi: String(item.guardrailKpi || "").trim(),
      priority,
    };
    if (!experiment.name || !experiment.hypothesis) return null;
    if (experiment.implementationSteps.length === 0) {
      experiment.implementationSteps = [
        "Define baseline for selected KPI.",
        "Ship one controlled variant.",
        "Measure outcomes against control.",
      ];
    }
    if (!experiment.primaryKpi) experiment.primaryKpi = "Primary conversion rate";
    if (!experiment.guardrailKpi) experiment.guardrailKpi = "Retention rate";
    return experiment;
  }

  private static normalizeExecutionStep(item: any): AdvancedExecutionStep | null {
    if (!item || typeof item !== "object") return null;
    const step: AdvancedExecutionStep = {
      weekLabel: String(item.weekLabel || "").trim(),
      objective: String(item.objective || "").trim(),
      tasks: this.normalizeStringArray(item.tasks, 8),
    };
    if (!step.weekLabel || !step.objective) return null;
    if (step.tasks.length === 0) {
      step.tasks = ["Define owner", "Ship scoped task", "Track KPI impact"];
    }
    return step;
  }

  private static normalizeOutput(input: any): AdvancedLabOutput {
    const experiments = Array.isArray(input?.experiments)
      ? input.experiments
          .map((item: any) => this.normalizeExperiment(item))
          .filter((item: AdvancedExperiment | null): item is AdvancedExperiment =>
            Boolean(item),
          )
      : [];

    const executionPlan = Array.isArray(input?.executionPlan)
      ? input.executionPlan
          .map((item: any) => this.normalizeExecutionStep(item))
          .filter((item: AdvancedExecutionStep | null): item is AdvancedExecutionStep =>
            Boolean(item),
          )
      : [];

    return {
      summary: String(input?.summary || "").trim() || "Advanced plan generated.",
      diagnosis: this.normalizeStringArray(input?.diagnosis, 10),
      strategy: this.normalizeStringArray(input?.strategy, 10),
      experiments:
        experiments.length > 0
          ? experiments
          : [
              {
                name: "Baseline and improve core conversion",
                hypothesis:
                  "Clarifying value proposition and reducing friction will improve primary conversion.",
                implementationSteps: [
                  "Document current funnel baseline.",
                  "Ship one high-signal conversion variant.",
                  "Measure primary and guardrail KPI changes.",
                ],
                primaryKpi: "Activation rate",
                guardrailKpi: "Day-7 retention",
                priority: "high",
              },
            ],
      executionPlan:
        executionPlan.length > 0
          ? executionPlan
          : [
              {
                weekLabel: "Week 1",
                objective: "Set baseline and align plan owners",
                tasks: [
                  "Confirm KPI definitions and data sources.",
                  "Prioritize top 3 opportunities by impact.",
                  "Create implementation tickets.",
                ],
              },
            ],
      assetDeliverables: this.normalizeStringArray(input?.assetDeliverables, 12),
      trackingPlan: {
        primaryMetrics: this.normalizeStringArray(
          input?.trackingPlan?.primaryMetrics,
          8,
        ),
        secondaryMetrics: this.normalizeStringArray(
          input?.trackingPlan?.secondaryMetrics,
          8,
        ),
        eventTracking: this.normalizeStringArray(input?.trackingPlan?.eventTracking, 14),
      },
      risks: this.normalizeStringArray(input?.risks, 8),
      nextActions: this.normalizeStringArray(input?.nextActions, 8),
    };
  }

  private static mapRun(data: any): AdvancedLabRun {
    const featureId = String(data?.featureId || DEFAULT_ADVANCED_FEATURE_ID) as AdvancedFeatureId;
    const feature = this.getFeatureDefinition(featureId);
    return {
      runId: String(data?.runId || this.generateId("adv")),
      userId: String(data?.userId || ""),
      appId: String(data?.appId || ""),
      appName: String(data?.appName || "Unknown App"),
      featureId,
      featureLabel: String(data?.featureLabel || feature.label),
      modelUsed: String(data?.modelUsed || AIService.getDefaultModel()),
      input: {
        ...DEFAULT_ADVANCED_INPUT,
        ...(data?.input || {}),
        channels: this.normalizeStringArray(data?.input?.channels, 8),
      },
      output: this.normalizeOutput(data?.output || {}),
      createdAt: this.toDate(data?.createdAt),
      savedAt: data?.savedAt ? this.toDate(data.savedAt) : undefined,
    };
  }

  private static buildPrompt(
    app: App,
    feature: AdvancedFeatureDefinition,
    input: AdvancedLabInput,
  ): string {
    return [
      `You are an expert growth strategist focused on "${feature.label}".`,
      "",
      "Feature objective:",
      feature.description,
      "",
      "Specialized instructions:",
      ...feature.promptInstructions.map((line) => `- ${line}`),
      "",
      "App context:",
      ...buildAppContextLines(app).map((line) => `- ${line}`),
      "",
      "Execution input:",
      `- Primary goal: ${input.primaryGoal || "N/A"}`,
      `- Target audience: ${input.targetAudience || "N/A"}`,
      `- Budget range: ${input.budgetRange || "N/A"}`,
      `- Timeline: ${input.timeline || "N/A"}`,
      `- Current metrics: ${input.currentMetrics || "N/A"}`,
      `- Constraints: ${input.constraints || "N/A"}`,
      `- Channels: ${
        input.channels.length > 0 ? input.channels.join(", ") : "N/A"
      }`,
      `- Notes: ${input.notes || "N/A"}`,
      "",
      "Output requirements:",
      "- Keep recommendations specific, practical, and measurable.",
      "- Do not include generic placeholders.",
      "- Every experiment must include one primary KPI and one guardrail KPI.",
      "- Return valid JSON only that matches the schema.",
    ].join("\n");
  }

  static async generatePlan(input: {
    userId: string;
    app: App;
    featureId: AdvancedFeatureId;
    formInput: AdvancedLabInput;
    model?: string;
  }): Promise<AdvancedLabRun> {
    const feature = this.getFeatureDefinition(input.featureId);
    const prompt = this.buildPrompt(input.app, feature, input.formInput);

    const raw = await AIService.generateJSON<any>(prompt, {
      model: input.model || undefined,
      useGoogleSearch: true,
      structuredOutputSchema: ADVANCED_OUTPUT_SCHEMA,
      structuredOutputName: "advanced_growth_output",
      temperature: 0.2,
      maxTokens: 4096,
    });

    return {
      runId: this.generateId("adv"),
      userId: input.userId,
      appId: input.app.appId,
      appName: input.app.name,
      featureId: feature.id,
      featureLabel: feature.label,
      modelUsed: input.model || AIService.getDefaultModel(),
      input: {
        ...DEFAULT_ADVANCED_INPUT,
        ...input.formInput,
        channels: this.normalizeStringArray(input.formInput.channels, 8),
      },
      output: this.normalizeOutput(raw),
      createdAt: new Date(),
    };
  }

  static async saveRun(run: AdvancedLabRun): Promise<string> {
    const existingQuery = query(
      collection(db, COLLECTION),
      where("userId", "==", run.userId),
      where("runId", "==", run.runId),
    );
    const snapshot = await getDocs(existingQuery);

    const payload = {
      ...run,
      savedAt: serverTimestamp(),
      input: {
        ...DEFAULT_ADVANCED_INPUT,
        ...run.input,
        channels: this.normalizeStringArray(run.input.channels, 8),
      },
      output: this.normalizeOutput(run.output),
    };

    if (!snapshot.empty) {
      await updateDoc(snapshot.docs[0].ref, payload);
      return snapshot.docs[0].id;
    }

    const docRef = await addDoc(collection(db, COLLECTION), {
      ...payload,
      createdAt: run.createdAt || serverTimestamp(),
    });
    return docRef.id;
  }

  static async getAppRuns(userId: string, appId: string): Promise<AdvancedLabRun[]> {
    const q = query(
      collection(db, COLLECTION),
      where("userId", "==", userId),
      where("appId", "==", appId),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((item) => this.mapRun(item.data()))
      .sort(
        (a, b) =>
          (b.savedAt || b.createdAt).getTime() - (a.savedAt || a.createdAt).getTime(),
      );
  }

  static async deleteRun(userId: string, runId: string): Promise<void> {
    const q = query(
      collection(db, COLLECTION),
      where("userId", "==", userId),
      where("runId", "==", runId),
    );
    const snapshot = await getDocs(q);
    await Promise.all(snapshot.docs.map((item) => deleteDoc(item.ref)));
  }

  static exportAsJson(run: AdvancedLabRun): string {
    return JSON.stringify(run, null, 2);
  }

  static exportAsMarkdown(run: AdvancedLabRun): string {
    const section = (title: string, rows: string[]) =>
      `## ${title}\n${rows.length > 0 ? rows.map((item) => `- ${item}`).join("\n") : "- N/A"}`;

    const experimentRows = run.output.experiments.map(
      (item, index) =>
        `### ${index + 1}. ${item.name}
- Hypothesis: ${item.hypothesis}
- Primary KPI: ${item.primaryKpi}
- Guardrail KPI: ${item.guardrailKpi}
- Priority: ${item.priority.toUpperCase()}
- Steps:
${item.implementationSteps.map((step) => `  - ${step}`).join("\n")}`,
    );

    const executionRows = run.output.executionPlan.map(
      (item) =>
        `### ${item.weekLabel}
- Objective: ${item.objective}
- Tasks:
${item.tasks.map((task) => `  - ${task}`).join("\n")}`,
    );

    return `# Advanced Growth Plan: ${run.featureLabel}

## App
- Name: ${run.appName}
- Feature: ${run.featureLabel}
- Model: ${run.modelUsed}
- Created: ${run.createdAt.toISOString()}

## Summary
${run.output.summary}

${section("Diagnosis", run.output.diagnosis)}

${section("Strategy", run.output.strategy)}

## Experiments
${experimentRows.length > 0 ? experimentRows.join("\n\n") : "- N/A"}

## Execution Plan
${executionRows.length > 0 ? executionRows.join("\n\n") : "- N/A"}

${section("Asset Deliverables", run.output.assetDeliverables)}

## Tracking Plan
### Primary Metrics
${run.output.trackingPlan.primaryMetrics
  .map((item) => `- ${item}`)
  .join("\n") || "- N/A"}

### Secondary Metrics
${run.output.trackingPlan.secondaryMetrics
  .map((item) => `- ${item}`)
  .join("\n") || "- N/A"}

### Event Tracking
${run.output.trackingPlan.eventTracking.map((item) => `- ${item}`).join("\n") || "- N/A"}

${section("Risks", run.output.risks)}

${section("Next Actions", run.output.nextActions)}
`.trim();
  }
}

export default AdvancedGrowthService;
